/**
 * De persoonlijke instellingen, voor iedereen: Profiel en Weergave. Ze staan in het
 * scherm Instellingen boven het beheer, dat alleen beheerders zien (zie Beheer.tsx).
 *
 * Weergave wordt per apparaat bewaard, net als de stand van de zijbalk: de werkvloer
 * en een bureau zijn verschillende plekken.
 */
import { useState } from 'react';

import type { Recht } from '../api';
import { initialen, useGegevens } from '../gegevens';

// ── Startscherm ──────────────────────────────────────────────────────────────

const STARTSCHERM = 'houtstaat.startscherm';

export const STARTSCHERMEN: Array<{ pad: string; label: string; recht: Recht }> = [
  { pad: '/dashboard', label: 'Dashboard', recht: 'dashboardBekijken' },
  { pad: '/houtstaat', label: 'Houtstaat', recht: 'houtstaatBekijken' },
  { pad: '/werkvloer', label: 'Werkvloer', recht: 'werkvloerBekijken' },
  { pad: '/planning', label: 'Planning', recht: 'planningBekijken' },
];

export function leesStartscherm(): (typeof STARTSCHERMEN)[number] | null {
  try {
    const pad = localStorage.getItem(STARTSCHERM);
    return STARTSCHERMEN.find((s) => s.pad === pad) ?? null;
  } catch {
    return null;
  }
}

// ── Animaties ────────────────────────────────────────────────────────────────

const ANIMATIES = 'houtstaat.animaties';
type Animaties = 'systeem' | 'aan' | 'uit';

const leesAnimaties = (): Animaties => {
  try {
    const waarde = localStorage.getItem(ANIMATIES);
    return waarde === 'aan' || waarde === 'uit' ? waarde : 'systeem';
  } catch {
    return 'systeem';
  }
};

/** Zet de keuze op `<html>`; de CSS leest `data-motion` (zie basis.css). Bij het starten aanroepen. */
export function pasAnimatiesToe(keuze: Animaties = leesAnimaties()): void {
  const root = document.documentElement;
  if (keuze === 'systeem') root.removeAttribute('data-motion');
  else root.setAttribute('data-motion', keuze === 'aan' ? 'on' : 'off');
}

// ── Schermdelen ──────────────────────────────────────────────────────────────

export function Profiel() {
  const { ik, profiel } = useGegevens();
  const naam = profiel?.weergavenaam ?? ik?.naam ?? '';
  return (
    <section className="hs-paneel hs-paneel__inhoud hs-instelling">
      <div className="hs-instelling__wie">
        {profiel?.fotoUrl ? (
          <img className="hs-avatar hs-avatar--groot hs-avatar--foto" src={profiel.fotoUrl} alt="" />
        ) : (
          <span className="hs-avatar hs-avatar--groot">{initialen(naam)}</span>
        )}
        <div>
          <p className="hs-instelling__naam">{naam}</p>
          <p className="hs-muted">{profiel?.email ?? ik?.naam}</p>
        </div>
      </div>
      <dl className="hs-instelling__lijst">
        <dt>Rol</dt>
        <dd>{ik?.rolLabel.join(', ') || 'Geen rol'}</dd>
        <dt>Aangemeld via</dt>
        <dd>Microsoft Entra ID</dd>
      </dl>
      <p className="hs-muted">
        Naam, e-mailadres en foto komen uit je Microsoft-account; de rol kent een beheerder toe.
      </p>
    </section>
  );
}

export function Weergave() {
  const { mag } = useGegevens();
  const [start, setStart] = useState(() => leesStartscherm()?.pad ?? '');
  const [animaties, setAnimaties] = useState(leesAnimaties);
  const schermen = STARTSCHERMEN.filter((s) => mag(s.recht));

  const bewaar = (sleutel: string, waarde: string) => {
    try {
      if (waarde) localStorage.setItem(sleutel, waarde);
      else localStorage.removeItem(sleutel);
    } catch {
      // Zonder opslag geldt de keuze tot de pagina herlaadt.
    }
  };

  return (
    <section className="hs-paneel hs-paneel__inhoud hs-instelling">
      <fieldset className="hs-instelling__groep">
        <legend>Startscherm</legend>
        <p className="hs-muted">Waar de applicatie opent na het aanmelden.</p>
        <Keuzes
          naam="startscherm"
          waarde={start}
          opties={[{ waarde: '', label: 'Standaard voor mijn rol' }, ...schermen.map((s) => ({ waarde: s.pad, label: s.label }))]}
          onKies={(pad) => {
            setStart(pad);
            bewaar(STARTSCHERM, pad);
          }}
        />
      </fieldset>
      <fieldset className="hs-instelling__groep">
        <legend>Animaties</legend>
        <p className="hs-muted">Uit houdt ook de knipperende markering van de huidige stap stil.</p>
        <Keuzes
          naam="animaties"
          waarde={animaties}
          opties={[
            { waarde: 'systeem', label: 'Volg systeem' },
            { waarde: 'aan', label: 'Aan' },
            { waarde: 'uit', label: 'Uit' },
          ]}
          onKies={(keuze) => {
            const nieuw = keuze as Animaties;
            setAnimaties(nieuw);
            bewaar(ANIMATIES, nieuw === 'systeem' ? '' : nieuw);
            pasAnimatiesToe(nieuw);
          }}
        />
      </fieldset>
    </section>
  );
}

/** Een rij keuzerondjes als knoppen, zoals de segmenten in de Gate Review-applicatie. */
function Keuzes({
  naam,
  waarde,
  opties,
  onKies,
}: {
  naam: string;
  waarde: string;
  opties: Array<{ waarde: string; label: string }>;
  onKies: (waarde: string) => void;
}) {
  return (
    <div className="hs-segment" role="radiogroup" aria-label={naam}>
      {opties.map((o) => (
        <button
          key={o.waarde}
          type="button"
          role="radio"
          aria-checked={waarde === o.waarde}
          className={`hs-segment__optie${waarde === o.waarde ? ' actief' : ''}`}
          onClick={() => onKies(o.waarde)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
